import React, { useState } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBBadge,
  MDBAlert,
} from "mdb-react-ui-kit";
import PopupLoader from "../views/popups/PopupLoader";
import { useTranslation } from "react-i18next";

const SMSStatusAlert = ({
  isOpen,
  onClose,
  status,
  campaignData = {},
  websiteVerificationStatus = null,
  handleAnyWaySubmit,
}) => {
  const { t } = useTranslation();
  const [failureAlert, setfailureAlert] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);

  const toggleFailure = () => setfailureAlert(!failureAlert);

  const getStatusConfig = () => {
    switch (status) {
      case "pending":
        return {
          icon: "clock",
          color: "warning",
          title: "SMS Campaign Status - Pending",
          badgeColor: "warning",
          badgeText: "Pending",
          description:
            "Your SMS campaign is currently under review. We will notify you once the verification process is complete.",
          showSpinner: true,
        };
      case "failure":
        return {
          icon: "exclamation-triangle",
          color: "danger",
          title: "SMS Campaign Status",
          badgeColor: "danger",
          badgeText: "Verification Failed",
          description:
            "Your SMS campaign verification has failed. Please review the requirements and resubmit.",
          showSpinner: false,
        };
      case "success":
        return {
          icon: "check-circle",
          color: "success",
          title: "SMS Campaign Status - Approved",
          badgeColor: "success",
          badgeText: "Approved",
          description:
            "Your SMS campaign has been successfully verified and approved.",
          showSpinner: false,
        };
      default:
        return {
          icon: "info-circle",
          color: "info",
          title: "SMS Campaign Status",
          badgeColor: "info",
          badgeText: "Unknown",
          description: "Status information is not available.",
          showSpinner: false,
        };
    }
  };

  const config = getStatusConfig();

  // Compliance Guidance Card Component
  const ComplianceGuidanceCard = ({ campaignData }) => {
    const reason = campaignData;
    let complianceData = null;

    // Parse reason if it's a string
    if (typeof reason === 'string') {
      try {
        complianceData = JSON.parse(reason);
      } catch (e) {
        console.error("Failed to parse reason:", e);
        return null;
      }
    } else if (typeof reason === 'object' && reason !== null) {
      complianceData = reason;
    } else {
      return null;
    }

    // Validate procedure exists
    if (!complianceData || !Array.isArray(complianceData.procedure) || complianceData.procedure.length === 0) {
      return null;
    }

    const { note, procedure, tcr_errors } = complianceData;

    return (
      <div className="p-3 border rounded-3 mb-4 compliance-guidance-card">
        {/* TCR Errors Summary */}
        {Array.isArray(tcr_errors) && tcr_errors.length > 0 && (
            <MDBAlert color="warning" className="mb-3 py-2">
              <MDBIcon fas icon="exclamation-circle" className="me-2" />
              <strong>Detected TCR Issues:</strong>
              <ul className="mb-0 mt-2">
              {tcr_errors.map((error, idx) => (
                <li key={idx}>{error}</li>
                ))}
              </ul>
            </MDBAlert>
          )}

        {/* Note Section */}
        {note && (
          <p className="note note-primary mb-3 compliance-guidance-note">{note}</p>
        )}

        {/* Main Title */}
        <h5 className="fw-bold mb-3 compliance-guidance-title">TCR 10DLC Compliance Guidance</h5>

        {/* Procedure Sections */}
        {procedure.map((section, index) => (
          <div
            key={index}
            className={`compliance-guidance-section ${index < procedure.length - 1 ? "border-bottom pb-3 mb-3" : "pb-3"}`}
          >
            {/* Section Title */}
            <h6 className="fw-bold mb-2 compliance-guidance-section-title">{section.title}</h6>

            {/* Steps */}
            {Array.isArray(section.steps) && section.steps.length > 0 && (
              <ol className="mb-3">
                {section.steps.map((step, stepIdx) => (
                  <li className="mb-2" key={stepIdx}>
                    {step}
                  </li>
                ))}
            </ol>
            )}

            {/* Example with Copy Button */}
            {section.example && (
                <div className="bg-secondary bg-opacity-10 p-3 rounded-3 mb-3 position-relative compliance-example-box">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="fw-500 mb-0 compliance-example-label">Example:</p>

                    <p
                      onClick={() => {
                      navigator.clipboard.writeText(section.example);
                      setCopiedItem(`example-${index}`);
                        setTimeout(() => setCopiedItem(null), 2000);
                      }}
                    className={`mb-0 p-2 rounded-5 cursor-pointer ${
                      copiedItem === `example-${index}` ? "hover-green" : "hover-primary"
                    }`}
                    style={{ cursor: "pointer" }}
                    >
                      <MDBIcon
                        fas
                      icon={copiedItem === `example-${index}` ? "check" : "copy"}
                      className="me-2"
                      color={copiedItem === `example-${index}` ? "success" : "primary"}
                      />
                    {copiedItem === `example-${index}` ? "Copied to clipboard!" : "Copy to clipboard"}
                    </p>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                  <p className="mb-0 pe-4 compliance-example-text" style={{ whiteSpace: "pre-line" }}>
                    {section.example}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <MDBModal open={isOpen} onClose={onClose} tabIndex="-1">
      <MDBModalDialog size="lg" centered className="sms-status-alert-modal">
        <MDBModalContent className="sms-status-alert">
          <MDBModalHeader>
            <MDBModalTitle className="d-flex align-items-center">
              <MDBIcon
                fas
                icon={config.icon}
                className={`me-2 text-${config.color}`}
                size="lg"
              />
              {config.title}
            </MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={onClose} />
          </MDBModalHeader>

          <MDBModalBody className="max-70vh">
            {/* Status Badge */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <p className="mb-0">Website Status:</p>
                <MDBBadge color={config.badgeColor} className="fs-6 px-3 py-2 ms-2">
                  {config.badgeText}
                </MDBBadge>
              </div>
            </div>

            {/* Campaign Information */}
            {campaignData && Object.keys(campaignData).length > 0 && (
              <div className="mb-4 border rounded-3 bg-light p-3">
                <h6 className="fw-bold mb-3 fs-5">Campaign Details</h6>
                <div className="row">
                  {campaignData.brand_name && (
                    <div className="col-md-6 mb-2">
                      <strong>Brand Name:</strong> {campaignData.brand_name}
                    </div>
                  )}
                  {campaignData.camp_name && (
                    <div className="col-md-6 mb-2">
                      <strong>Campaign Name:</strong> {campaignData.camp_name}
                    </div>
                  )}
                  {campaignData.status && (
                    <div className="col-md-6 mb-2">
                      <strong>Current Status:</strong>{" "}
                      <span className="text-capitalize">{campaignData.status}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status Description */}
            <MDBAlert color={config.color} className="mb-4">
              <MDBIcon fas icon={config.icon} className="me-2" />
              {config.description}
            </MDBAlert>

            {/* Additional Information for Failure State */}
            {status === "failure" && (
              <div className="mt-4">
                  <ComplianceGuidanceCard campaignData={campaignData} />
              </div>
            )}

            {/* Additional Information for Pending State */}
            {status === "pending" && (
              <div className="mt-4">
                <MDBAlert color="info" className="mb-3">
                  <MDBIcon fas icon="info-circle" className="me-2" />
                  <strong>What happens next?</strong>
                  <ul className="mb-0 mt-2">
                    <li>Our team will review your campaign details</li>
                    <li>We will verify your website compliance</li>
                    <li>You will receive an email notification once complete</li>
                    <li>Typical review time is 1-2 business days</li>
                  </ul>
                </MDBAlert>
              </div>
            )}
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={onClose}>
              Close
            </MDBBtn>
            {status === "failure" && (
            <MDBBtn
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                toggleFailure();
              }}
            >
              Submit Anyway
            </MDBBtn>
            )}
          </MDBModalFooter>

          {/* Submit Anyway Confirmation Modal */}
          {failureAlert && (
              <PopupLoader
                title="Alert"
                content={
                  <>
                    <div className="pb-2 d-flex flex-column h-100">
                      <div className="text-center mb-4 flex-grow-1">
                        <p className="m-0">
                        Proceeding with this action will bypass website verification.
                        </p>
                      <h4 className="m-0">Are you sure you want to continue?</h4>
                      </div>
                      <div className="text-center">
                      <b>Note:</b> Bypassing verification may cause TCR to reject the campaign.
                      </div>
                    </div>
                    <MDBModalFooter className="pe-0 pb-0">
                      <MDBBtn onClick={toggleFailure} color="secondary">
                        {t("cancel")}
                      </MDBBtn>
                      <MDBBtn
                        onClick={(e) => {
                          e.preventDefault();
                          handleAnyWaySubmit();
                        }}
                      >
                        {t("Proceed")}
                      </MDBBtn>
                    </MDBModalFooter>
                  </>
                }
                onShow={setfailureAlert}
                onClose={toggleFailure}
              />
          )}
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
};

export default SMSStatusAlert;